import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { Screen } from "../components/Screen";
import { useToast } from "../components/Toast";
import { relativeTime } from "../lib/relative";
import { useDocumentTitle } from "../lib/title";
import { useCardList } from "../lib/useCardList";
import { useInfiniteScroll } from "../lib/useInfiniteScroll";
import { useStatusChange } from "../lib/useStatusChange";

export function Dismissed() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t("dismissed.title"));
  const toast = useToast();
  const list = useCardList({ status: "dismissed" });
  const change = useStatusChange(list);
  const [confirming, setConfirming] = useState(false);

  const shown = list.cards.filter((card) => card.status === "dismissed");
  const sentinel = useInfiniteScroll(list.hasMore, list.loadMore, shown.length);
  const count = `${shown.length}${list.hasMore ? "+" : ""}`;

  async function purge() {
    setConfirming(false);
    try {
      const { purged } = await api.cards.purgeDismissed();
      toast.show(t("dismissed.purged", { count: purged }));
      list.reload();
    } catch {
      toast.show(t("errors.save"));
    }
  }

  return (
    <Screen>
      <header className="page-head">
        <div>
          <h1 className="title" tabIndex={-1}>{t("dismissed.title")}</h1>
          <p className="subtitle">{t("dismissed.count", { count })}</p>
        </div>
        {shown.length > 0 && !confirming && (
          <button type="button" className="link-button link-danger" onClick={() => setConfirming(true)}>
            {t("dismissed.purgeAll")}
          </button>
        )}
      </header>

      {confirming && (
        <div className="notice notice-danger" role="alertdialog" aria-label={t("dismissed.purgeAll")}>
          <span>{t("dismissed.purgeQuestion")}</span>
          <div className="notice-actions">
            <button type="button" className="pill pill-danger" onClick={() => void purge()} autoFocus>
              {t("dismissed.purgeConfirm")}
            </button>
            <button type="button" className="pill pill-quiet" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {list.error && (
        <div className="notice" role="alert">
          <span>{t("errors.load")}</span>
          <button type="button" className="link-button" onClick={list.reload}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {list.loading && <p className="status">{t("loading")}</p>}
      {!list.loading && !list.error && shown.length === 0 && <p className="status">{t("dismissed.empty")}</p>}

      <ul className="stack rows">
        {shown.map((card) => (
          <li key={card.id} className="row">
            <div className="row-text">
              <div className="row-top">
                <span className="badge badge-quiet" dir="auto">
                  {card.type}
                </span>
                <span className="row-time">{relativeTime(card.created_at, new Date(), i18n.language)}</span>
              </div>
              <p className="row-summary" dir="auto">
                {card.summary}
              </p>
            </div>
            <button type="button" className="pill pill-ink" onClick={() => void change(card, "unread")}>
              {t("dismissed.restore")}
            </button>
          </li>
        ))}
      </ul>
      {list.hasMore && <div ref={sentinel} className="sentinel" aria-hidden="true" />}
      {list.loadingMore && <p className="status">{t("loading")}</p>}

      <p className="note">{t("dismissed.note")}</p>
    </Screen>
  );
}
